// Ported from dev-scrips-pm-react src/patients/services/read-emirates-id-via-agent.ts (PROD-1716).
/**
 * PROD-1289 (PM) / PROD-1716 (PA) — read an Emirates ID via the local headless agent
 * (Option 1), a pull-based sibling of the desktop-app → backend → SignalR relay path
 * (`use-emirates-id-scanner.ts`).
 *
 * Flow:
 *   1. Ask OUR backend for a short-lived, single-use capability nonce (authenticated session).
 *   2. Call the localhost agent's `/read-card` with that nonce as a Bearer token.
 *   3. Return the raw {@link EmiratesIdCardData} (camelCase, same shape the SignalR feed delivered),
 *      so the existing parser (`parseEmiratesIdCardData`) + form-fill are reused unchanged.
 *
 * The agent runs on the reception PC over loopback (http://127.0.0.1). The browser reaches it via
 * Private Network Access — no cert. The agent's Origin allowlist must include this app's origin,
 * and the nonce closes the "any local process can hit the port" gap. See ADR-EIDA-001.
 *
 * NOTE: not yet wired into the live scan trigger — it's the drop-in for `useEmiratesIdScanner`
 * once the agent is deployed + the Phase-1 spike passes. Gate the swap behind config.
 *
 * PHI-in-transit hardening (review follow-up, PROD-1703): the agent host is a HARDCODED
 * loopback literal, NOT sourced from an env var. A `VITE_EID_AGENT_*` override would let a
 * build-time config value point the browser's PHI request at an attacker-controlled remote
 * host instead of the local reader — the whole point of the loopback + capability-nonce
 * design is that the destination can't be redirected. Do NOT reintroduce an env-configurable
 * agent URL/host.
 */
import type { EmiratesIdCardData } from '@/shared/types/emirates-id';

const AGENT_URL = 'http://127.0.0.1:47600' as const;
const API_BASE: string = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

export type EidAgentErrorCode =
  | 'no_session' // missing access token
  | 'issue_failed' // backend wouldn't mint a capability nonce
  | 'agent_unreachable' // couldn't reach the localhost agent (not installed / not running / PNA blocked)
  | 'no_reader' // agent up, no PC/SC reader
  | 'no_card' // reader present, no card inserted
  | 'read_failed'; // agent/SDK error

export class EidAgentError extends Error {
  readonly code: EidAgentErrorCode;
  readonly status?: number;
  constructor(code: EidAgentErrorCode, message: string, status?: number) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

/** Mint the single-use capability nonce from our backend for the authenticated session. */
async function issueCapability(accessToken: string): Promise<string> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/Account/IssueReadCapability`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  } catch (e) {
    throw new EidAgentError('issue_failed', `Could not reach backend: ${String(e)}`);
  }
  if (!res.ok) {
    throw new EidAgentError('issue_failed', 'Backend refused to issue a read capability.', res.status);
  }
  const body = (await res.json()) as { token?: string };
  if (!body?.token) throw new EidAgentError('issue_failed', 'Backend returned no capability token.');
  return body.token;
}

/** Map the agent's non-200 read errors to typed codes (see EidToolkitReader / ReadCardServer). */
function mapAgentReadError(status: number, reason?: string): EidAgentError {
  if (status === 409 || reason === 'NoCard') return new EidAgentError('no_card', 'No card in the reader.', status);
  if (status === 503 || reason === 'NoReader' || reason === 'NotInitialized')
    return new EidAgentError('no_reader', 'No reader / agent not ready.', status);
  return new EidAgentError('read_failed', reason ?? 'Card read failed.', status);
}

/**
 * Reads the inserted Emirates ID via the local agent. Throws {@link EidAgentError} on any failure
 * so the caller can show a precise message (insert card / connect reader / agent not installed).
 */
export async function readEmiratesIdViaAgent(accessToken: string | null | undefined): Promise<EmiratesIdCardData> {
  if (!accessToken) throw new EidAgentError('no_session', 'Not signed in.');

  const capability = await issueCapability(accessToken);

  let res: Response;
  try {
    res = await fetch(`${AGENT_URL}/read-card`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${capability}` },
    });
  } catch (e) {
    // Network/PNA failure, or the agent isn't installed/running.
    throw new EidAgentError('agent_unreachable', `EID agent not reachable at ${AGENT_URL}: ${String(e)}`);
  }

  if (!res.ok) {
    let reason: string | undefined;
    try {
      reason = ((await res.json()) as { error?: string })?.error;
    } catch {
      /* non-JSON error body */
    }
    throw mapAgentReadError(res.status, reason);
  }

  return (await res.json()) as EmiratesIdCardData;
}
